import eventApi from '_api/event'
import groupApi from '_api/group'
import orderApi from '_api/order'
import reportApi from '_api/report'
import {
  EVENT_PAY_LATER,
  EVENT_STATUS_OPEN,
  GROUP_IS_PRIVATE,
  LIST_TIME,
} from '_utils/constant'
import { convertToCurrentGMT } from '_utils/function'
import { getUserInfo } from '_utils/localData'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useToasts } from 'react-toast-notifications'

import { convertProducts, convertProductsWhenEdit } from '../utils/productTransforms'

/**
 * Owns fetching the event/group (or shared-event) and derives every flag the
 * page needs from that data. Same source data & branching as
 * EventDetail/index.js#getEventDetail/getGroupDetail/getSharedEvent/getListOrderByRef.
 */
const useEventDetailData = ({ eid, oid, shareCode, sharedId }) => {
  const { addToast } = useToasts()
  const edittingOrder = !!eid && !!oid && !shareCode

  const [isLoading, setIsLoading] = useState(false)
  const [noEventFound, setNoEventFound] = useState(false)
  const [eventDetail, setEventDetail] = useState({})
  const [groupDetail, setGroupDetail] = useState({})
  const [editOrderBookingAddress, setEditOrderBookingAddress] = useState(null)
  const [editOrderDeliveryDate, setEditOrderDeliveryDate] = useState(null)
  const [collectionTimeStart, setCollectionTimeStart] = useState('')
  const [collectionTimeEnd, setCollectionTimeEnd] = useState('')
  const [checkShareEventHaveGroupIsPrivate, setCheckShareEventHaveGroupIsPrivate] = useState(false)
  const [productInit, setProductInit] = useState(null)
  const [initialComment, setInitialComment] = useState('')

  const getEventDetail = async () => {
    try {
      const res = await eventApi.getEventDetail({ eid, isGetProducts: true, isGetBuyerNames: true })
      if (res) {
        setEventDetail(res.msgResp || {})
        return {
          groupId: res.msgResp.groupId,
          productsDetail: res.msgResp.products,
        }
      }
    } catch (_error) {
      setNoEventFound(true)
      reportApi.report({
        message: `[Event details 01]${_error.msgCode} - [Event detail] #getEventDetail ${_error.msgResp}`,
      })
      addToast(_error.msgResp, { appearance: 'error', autoDismiss: true })
    }
    setIsLoading(false)
  }

  const getGroupDetail = async (gid = '') => {
    try {
      const res = await groupApi.getMemberInfo(gid)
      if (res) {
        setGroupDetail(res.msgResp || {})
      }
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong!', { appearance: 'error', autoDismiss: true })
    }
  }

  const getListOrderByRef = async () => {
    try {
      const res = await orderApi.getOrderByRef(oid)
      if (res) {
        if (res.msgResp && res.msgResp.length > 0) {
          setEditOrderBookingAddress(res.msgResp[0].bookingAddress)
          setEditOrderDeliveryDate(res.msgResp[0].deliveryDate || null)
        }
        return res.msgResp
      }
    } catch (err) {
      addToast(err?.msgResp || 'Something went wrong!', { appearance: 'error', autoDismiss: true })
    }
  }

  const initialState = async () => {
    try {
      const { groupId = '', productsDetail = {} } = (await getEventDetail()) || {}
      if (groupId && productsDetail) {
        if (edittingOrder) {
          const listOrderByRef = await getListOrderByRef()
          const convertedProductInit = convertProductsWhenEdit(productsDetail, listOrderByRef)
          setProductInit(convertedProductInit)
          setInitialComment(listOrderByRef?.[0]?.comment || '')
        } else {
          setProductInit({
            newOrderProducts: convertProducts(productsDetail),
            productOrderRender: productsDetail.map((p) => ({
              ...p,
              index: 1,
              newOrderCount: (p.defaultQuantity || 0) + (p.orderCount || 0),
            })),
            productOrderRenderIndividual: [],
            newOrderProductsIndividual: [],
            quantities: {},
            initTotalOrderCount: 0,
          })
        }
        await getGroupDetail(groupId)
      }
    } catch (err) {
      console.error('[EventDetailData] initialState error:', err)
      addToast(err?.msgResp || 'Something went wrong!', { appearance: 'error', autoDismiss: true })
    } finally {
      setIsLoading(false)
    }
  }

  const getSharedEvent = async () => {
    try {
      const res = await eventApi.getShareEvent({ eventId: eid, shareCode })
      if (res && res.msgCode % 100 === 0) {
        const { msgResp: _eventDetail } = res || {}
        const { products: _products } = _eventDetail
        setEventDetail(res.msgResp || {})
        setGroupDetail({ group: res.msgResp.group })
        setProductInit({
          newOrderProducts: convertProducts(_products),
          productOrderRender: _products.map((p) => ({
            ...p,
            index: 1,
            newOrderCount: p.defaultQuantity + p.orderCount,
          })),
          productOrderRenderIndividual: [],
          newOrderProductsIndividual: [],
          quantities: {},
          initTotalOrderCount: 0,
        })
      }
    } catch (err) {
      setNoEventFound(true)
      reportApi.report({
        message: `[Event details 02]${err.msgCode} - [Event detail] #getSharedEvent ${err.msgResp}`,
      })
      addToast(err?.msgResp || 'Something went wrong! 4', { appearance: 'error', autoDismiss: true })
    }
    setIsLoading(false)
  }

  const getSharedEventBySharedId = async () => {
    try {
      const res = await eventApi.getSharedEventNoAuth({ sharedId, isGetProducts: true })
      if (res && res.msgCode === 75100) {
        const _eventDetail = res.msgResp || {}
        const _products = _eventDetail.products || []
        setEventDetail(_eventDetail)
        setGroupDetail({
          group: {
            id: _eventDetail.groupId,
            name: _eventDetail.groupName,
            totalEvents: _eventDetail.totalEvents,
            totalMembers: _eventDetail.totalMembers,
            question: _eventDetail.question,
          },
        })
        setProductInit({
          newOrderProducts: convertProducts(_products),
          productOrderRender: _products.map((p) => ({
            ...p,
            index: 1,
            newOrderCount: p.defaultQuantity + p.orderCount,
          })),
          productOrderRenderIndividual: [],
          newOrderProductsIndividual: [],
          quantities: {},
          initTotalOrderCount: 0,
        })
      } else {
        setNoEventFound(true)
        addToast(res?.msgResp || 'Invalid or expired share link', { appearance: 'error', autoDismiss: true })
      }
    } catch (err) {
      setNoEventFound(true)
      addToast(err?.msgResp || 'Something went wrong!', { appearance: 'error', autoDismiss: true })
    }
    setIsLoading(false)
  }

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setIsLoading(true)
      if (sharedId) {
        getSharedEventBySharedId()
      } else if (!shareCode) {
        initialState()
      } else {
        getSharedEvent()
      }
    }
    return () => {
      mounted = false
    }
    // Deliberately runs once on mount only.
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (eventDetail && eventDetail.pickupDuration && eventDetail.pickupTime) {
      const pickupTimeCurrentGMT = convertToCurrentGMT(eventDetail.pickupTime)
      const timeStart = Number(pickupTimeCurrentGMT.split(' ')[1].split(':')[0])
      const timeEnd = timeStart + eventDetail?.pickupDuration / 3600
      if (timeEnd <= 24) {
        const indexStart = LIST_TIME.findIndex((item) => item.id === timeStart)
        const indexEnd = LIST_TIME.findIndex((item) => item.id === timeEnd % 24)
        setCollectionTimeStart(LIST_TIME[indexStart]?.text)
        setCollectionTimeEnd(LIST_TIME[indexEnd]?.text)
      } else {
        setCollectionTimeStart('')
        setCollectionTimeEnd('')
      }
    }
  }, [eventDetail])

  useEffect(() => {
    if (sharedId) {
      setCheckShareEventHaveGroupIsPrivate(false)
      return
    }

    const listGroupOfUser = getUserInfo()?.groups
    if (groupDetail.group?.public === GROUP_IS_PRIVATE) {
      const checkUserHaveThisGroup = listGroupOfUser?.filter(
        (group) => group?.gid === groupDetail?.group?.id
      )
      setCheckShareEventHaveGroupIsPrivate(!checkUserHaveThisGroup?.length)
    } else {
      setCheckShareEventHaveGroupIsPrivate(false)
    }
  }, [groupDetail, sharedId])

  const payLater = eventDetail.paynow === EVENT_PAY_LATER
  const adminOrHost = !!groupDetail?.isAdmin || !!groupDetail?.isHost
  const isAllowManageEvent =
    !!groupDetail?.isAdmin || eventDetail?.createdUserId === groupDetail?.uid

  const allowPlaceOrder = useMemo(
    () =>
      eventDetail.status === EVENT_STATUS_OPEN &&
      moment(convertToCurrentGMT(eventDetail.closingTime), 'DD-MM-YYYY HH:mm').isAfter(
        moment(new Date())
      ),
    [eventDetail.status, eventDetail.closingTime]
  )

  const listImages = useMemo(
    () => eventDetail.photoUrls?.map((item) => ({ original: item })) || [],
    [eventDetail.photoUrls]
  )

  return {
    isLoading,
    noEventFound,
    eventDetail,
    groupDetail,
    edittingOrder,
    payLater,
    adminOrHost,
    isAllowManageEvent,
    allowPlaceOrder,
    collectionTimeStart,
    collectionTimeEnd,
    checkShareEventHaveGroupIsPrivate,
    editOrderBookingAddress,
    editOrderDeliveryDate,
    productInit,
    initialComment,
    listImages,
  }
}

export default useEventDetailData
